'use client'

import {JWTPayload} from 'jose'
import {observer} from 'mobx-react'
// @ts-ignore
import {useRouter} from 'nextjs-toploader/app'
import React, {useEffect, useMemo, useState} from 'react'
import {useFormContext} from 'react-hook-form'
import {toast} from 'react-toastify'
import {z} from 'zod'
import {useMutation} from '@tanstack/react-query'

import {zFormRules} from '@/src/config/validation'

import {Shop} from '@/src/entities/shop'

import {FormTemplate} from '@/src/shared/modules/form/ui/template'
import {useFormTesting} from '@/src/features/functional-testing'
import {FormTestingRoutes} from '@/src/features/functional-testing/model/functional-testing-vm'

import {useStore} from '@/src/shared/hooks/useStore'
import {InvisibleCaptcha, useCaptcha} from '@/src/shared/modules/captcha'
import Button from '@/src/shared/ui/button'
import {FormCheckBox} from '@/src/shared/ui/inputs/checkbox'
import FormPhoneInput from '@/src/shared/ui/inputs/form-phone-input'
import {FormInput} from '@/src/shared/ui/inputs/input'
import {Label} from '@/src/shared/ui/label'
import {AsyncProcessLoading} from '@/src/shared/ui/layout/ui/async-loading'
import Modal from '@/src/shared/ui/modal'
import {getClient} from '@/src/shared/utils/api-factory'

import * as UI from './personal-form.styled'
import {usePathname} from 'next/navigation'

import {Routes} from '@/src/config/routes'
import {handleMutationException} from '../../../../../../web/src/shared/utils/error'

type PersonalFormData = {
  name: string
  last_name: string
  phone: string
  email: string
  agree: boolean
  comment?: string
  // notify_by_sms: boolean
  subscribed: boolean
  lab_id: Id
  start_at?: string
  type_id: Id

  captcha_token: string
}

export const PersonalFormSchema = z.object({
  name: z.string().min(1, 'Обязательное поле').max(50, 'Максимум 50 символов'),
  last_name: z.string().min(1, 'Обязательное поле').max(50, 'Максимум 50 символов'),
  phone: zFormRules.phone,
  email: zFormRules.email.refine(email => email && email.length > 0, 'Обязательное поле'),
  agree: z.boolean().refine(agree => agree, 'Для записи нужно ваше согласие'),
  // notify_by_sms: z.boolean(),
  subscribed: z.boolean(),
  comment: z.string().nullable().optional()
})

interface PersonalFormProps {
  routes: FormTestingRoutes
  onSuccess?: (data: PersonalFormData) => void
}

export const PersonalForm = observer(({routes, onSuccess}: PersonalFormProps) => {
  const pathname = usePathname()

  const [success, setSuccess] = useState(false)
  const {token, onSuccessToken, visibleCaptcha, setVisibleCaptcha, renderKey, reset} = useCaptcha()
  const testingFormStore = useFormTesting()
  const router = useRouter()
  const form = useFormContext<PersonalFormData>()
  const {pageData} = useStore<{labs: Shop[]}>()
  const {labs} = pageData!.content

  const isFuncTesting = pathname === Routes.service.functionalTesting

  useEffect(() => {
    form.setValue('type_id', testingFormStore.typeId!)
    form.setValue('lab_id', testingFormStore.shopId!)
    form.setValue('start_at', testingFormStore.time?.start_at)
  }, [testingFormStore.typeId, testingFormStore.shopId, testingFormStore.time])

  const submitMutation = useMutation({
    mutationFn: async (data: PersonalFormData) => {
      const client = getClient()

      await client.post<Nullable<JWTPayload>>(routes.save, {
        data,
      })

      return data
    },

    async onSuccess(data) {
      toast.success('Вы успешно записаны')

      if (onSuccess) {
        onSuccess(data)
      }
      setSuccess(true)
    },
    async onError(error) {
      handleMutationException(error)
      reset()
    },
    async onSettled() {
      reset()
    },
  })

  const labId = testingFormStore.shopId
  const date = testingFormStore.date
  const time = testingFormStore.time

  const formTitle = useMemo(() => {
    let recordInfo = 'не выбрано'

    const shop = labs.find(shop => shop.id === labId)
    if (shop) {
      recordInfo = `${shop.title}, ${shop.address}`

      if (date && time) {
        recordInfo += `, ${date.toDate().toLocaleDateString()} в ${time.title}`
      }
    }
    return `Лаборатория бега: ${recordInfo}`
  }, [labId, date, time])

  const onSubmit = form.handleSubmit(data => {
    submitMutation.mutate({...data, captcha_token: token!})
  })

  return (
    <>
      <InvisibleCaptcha
        key={renderKey}
        renderKey={renderKey}
        onSuccess={onSuccessToken}
        visible={visibleCaptcha}
        setVisible={setVisibleCaptcha}
      />
      <FormTemplate title={formTitle}>
        <UI.Wrapper>
          <UI.Fields>
            <UI.FieldsRow>
              <FormInput name="name" required>
                Имя
              </FormInput>

              <FormInput name="last_name" required>
                Фамилия
              </FormInput>
            </UI.FieldsRow>
            <UI.FieldsRow>
              <UI.FieldsColumns>
                <FormPhoneInput name="phone" required>
                  Телефон
                </FormPhoneInput>
              </UI.FieldsColumns>
              <FormInput
                name="email"
                type="email"
                required
                info="E-mail необходим для отправки результатов тестирования.">
                Электронная почта
              </FormInput>
            </UI.FieldsRow>

            <UI.FieldsRow>
              <FormInput name="comment" type="textarea">
                Комментарий
              </FormInput>
            </UI.FieldsRow>

            <UI.FieldsRow>
              <FormCheckBox name="other-country-phone">У меня иностранный номер</FormCheckBox>
            </UI.FieldsRow>

            <div>
              <UI.FieldsRow>
                <FormCheckBox name="agree">
                  Cогласен с условиями проведения тестирования и пользовательским соглашением*
                </FormCheckBox>
              </UI.FieldsRow>

              {/*<UI.FieldsRow>*/}
              {/*  <FormCheckBox name="notify_by_sms">Напомнить по смс</FormCheckBox>*/}
              {/*</UI.FieldsRow>*/}

              <UI.FieldsRow>
                <FormCheckBox name="subscribed">Xочу получать рассылку</FormCheckBox>
              </UI.FieldsRow>

              <UI.Label>
                <Label>* Пункты, отмеченные звездочкой, обязательны к заполнению.</Label>
              </UI.Label>
            </div>
          </UI.Fields>
          <UI.Actions>
            <Button onClick={onSubmit} aria-label="submit" disabled={!token} loading={!token}>
              Записаться
            </Button>
          </UI.Actions>
        </UI.Wrapper>
      </FormTemplate>

      <Modal
        open={success}
        onCancel={() => {
          setSuccess(false)
          router.refresh()
        }}
        closeIcon={false}>
        <UI.ModalContainer>
          <UI.SuccessTitle>
            Вы успешно записаны на{' '}
            {isFuncTesting ? 'функциональное тестирование!' : 'подбор обуви!'}
          </UI.SuccessTitle>

          <UI.SuccessClose
            onClick={() => {
              setSuccess(false)
              router.push(Routes.profile.service.records.page)
            }}>
            <Button>Закрыть</Button>
          </UI.SuccessClose>
        </UI.ModalContainer>
      </Modal>

      {submitMutation.isPending && <AsyncProcessLoading />}
    </>
  )
})
